/**
 * Changing your own password.
 *
 * Two places use it. The settings page, for anyone who wants to. And the
 * prompt AppShell raises when `mustChangePassword` is set — after a hospital
 * admin reset the password to the username, or typed one in themselves — so a
 * password somebody else knows does not stay in use past the first sign-in.
 *
 * The prompt can be postponed ("لاحقاً"), not dismissed for good: it comes
 * back on the next load until the flag clears. Locking a receptionist out of
 * the desk mid-shift because of a form would be worse than the risk it
 * addresses.
 */

import { useState } from 'react';
import { Button, Field, Icon, Input, Modal } from '../../components/common';
import { authApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';

/** How strong, 0–4, by length and character variety. Presentation only. */
export function passwordStrength(value) {
  const v = String(value || '');
  let score = 0;
  if (v.length >= 6) score += 1;
  if (v.length >= 10) score += 1;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) score += 1;
  if (/\d/.test(v) && /[^A-Za-z0-9]/.test(v)) score += 1;
  return score;
}

const STRENGTH = [
  { label: 'ضعيفة جداً', tone: 'bg-red-500' },
  { label: 'ضعيفة', tone: 'bg-orange-500' },
  { label: 'متوسطة', tone: 'bg-amber-400' },
  { label: 'جيدة', tone: 'bg-emerald-400' },
  { label: 'قوية', tone: 'bg-cyan-400' },
];

export function ChangePasswordForm({ onChanged }) {
  const { success, error: toastError } = useNotifications();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const score = passwordStrength(next);
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready = current && next.length >= 6 && confirm === next;

  const submit = async () => {
    setBusy(true);
    try {
      await authApi.changePassword(current, next);
      success('تم تغيير كلمة المرور');
      setCurrent('');
      setNext('');
      setConfirm('');
      onChanged?.();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const type = show ? 'text' : 'password';
  return (
    <div className="space-y-4">
      <Field label="كلمة المرور الحالية" required>
        <Input dir="ltr" type={type} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="كلمة المرور الجديدة" required hint="6 أحرف على الأقل، ولا تكون مثل اسم المستخدم">
          <Input dir="ltr" type={type} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="تأكيد كلمة المرور" required error={mismatch ? 'غير متطابقة' : undefined}>
          <Input dir="ltr" type={type} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
      </div>
      {next && (
        <div className="flex items-center gap-3" aria-live="polite">
          <div className="flex flex-1 gap-1">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < score ? STRENGTH[score].tone : 'bg-white/10'}`} />
            ))}
          </div>
          <span className="text-xs text-slate-300">{STRENGTH[score].label}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300" onClick={() => setShow((v) => !v)}>
          <Icon name={show ? 'eye-off' : 'eye'} size={14} /> {show ? 'إخفاء' : 'إظهار'} كلمات المرور
        </button>
        <Button icon="key-round" loading={busy} disabled={!ready} onClick={submit}>
          تغيير كلمة المرور
        </Button>
      </div>
    </div>
  );
}

/** The prompt after a reset. Rendered by AppShell while the flag is set. */
export function PasswordChangePrompt() {
  const { mustChangePassword, refresh } = usePermissions();
  const [later, setLater] = useState(false);
  if (!mustChangePassword || later) return null;
  return (
    <Modal open onClose={() => setLater(true)} title="اختر كلمة مرور جديدة" icon="shield-alert" size="md">
      <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
        <Icon name="key-round" size={18} className="mt-0.5 shrink-0 text-amber-300" />
        <p>كلمة مرورك الحالية حددتها إدارة المستشفى (أو أُعيد تعيينها لتصبح اسم المستخدم). اختر كلمة مرور خاصة بك قبل متابعة العمل.</p>
      </div>
      <ChangePasswordForm onChanged={refresh} />
      <div className="mt-4 text-center">
        <button type="button" className="text-xs text-slate-500 underline hover:text-slate-300" onClick={() => setLater(true)}>
          لاحقاً
        </button>
      </div>
    </Modal>
  );
}
